import { Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { StartSession } from './pages/start-session/start-session.component';
import { NotFound } from './pages/not-found/not-found.component';
import { ThankYou } from './pages/thank-you/thank-you.component';
import { CellulaProvider, LayoutProvider, Spacer } from '@cellula/react';
import { primitiveColorTokens, theme, tokens } from '@cellula/react-theme-patient';
import styled from 'styled-components';
import { QrHook } from './pages/qr-hook/qr-hook.component';
import { Session } from './pages/session/session.component';
import { KioskSessionFlow, SessionController } from './session.controller';
import { NewContainer, useResolve } from '@visiba/instantiation';
import { sharedResolution } from './shared_resolution';

export const IS_MOBILE =
	!!/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile|ipad|android|android 3.0|xoom|sch-i800|playbook|tablet|kindle/i.test(
		navigator.userAgent,
	);

const licenceHolderService: { theme: { [name: string]: string } } = {
	theme: {},
};

function App() {
	return (
		<CellulaProvider
			providedInRoot
			theme={theme}
			tokenOverrides={{
				colorPrimary100: (licenceHolderService.theme['primary_100'] as string) ?? '#F8F8F9',
				colorPrimary200: (licenceHolderService.theme['primary_200'] as string) ?? '#EFEFF1',
				colorPrimary300: (licenceHolderService.theme['primary_300'] as string) ?? '#E1E2E5',
				colorPrimary400: (licenceHolderService.theme['primary_400'] as string) ?? '#D1D3D7',
				colorPrimary500: (licenceHolderService.theme['primary_500'] as string) ?? '#B3B7BE',
				colorPrimary600: (licenceHolderService.theme['primary_600'] as string) ?? '#9095A0',
				colorPrimary700: (licenceHolderService.theme['primary_700'] as string) ?? '#6A6F7B',
				colorPrimary800: (licenceHolderService.theme['primary_800'] as string) ?? '#555962',
				colorPrimary800t08: ((licenceHolderService.theme['primary_800'] as string) ?? '#555962') + '14', // Adding opacity 8% from primary 800
				colorPrimary800t12: ((licenceHolderService.theme['primary_800'] as string) ?? '#555962') + '1F', // Adding opacity 12% from primary 800
				colorPrimary800t60: ((licenceHolderService.theme['primary_800'] as string) ?? '#555962') + '99', // Adding opacity 60% from primary 800
				colorPrimary900: (licenceHolderService.theme['primary_900'] as string) ?? '#464951',
				colorPrimary1000: (licenceHolderService.theme['primary_1000'] as string) ?? '#2F3136',
				colorAccent100: (licenceHolderService.theme['accent_100'] as string) ?? '#F8F8F9',
				colorAccent200: (licenceHolderService.theme['accent_200'] as string) ?? '#EFEFF1',
				colorAccent300: (licenceHolderService.theme['accent_300'] as string) ?? '#E1E2E5',
				colorAccent400: (licenceHolderService.theme['accent_400'] as string) ?? '#D1D3D7',
				colorAccent500: (licenceHolderService.theme['accent_500'] as string) ?? '#B3B7BE',
				colorAccent600: (licenceHolderService.theme['accent_600'] as string) ?? '#9095A0',
				colorAccent700: (licenceHolderService.theme['accent_700'] as string) ?? '#6A6F7B',
				colorAccent800: (licenceHolderService.theme['accent_800'] as string) ?? '#555962',
				colorAccent800t08: ((licenceHolderService.theme['accent_800'] as string) ?? '#555962') + '14', // Adding opacity 8% from accent 800
				colorAccent800t12: ((licenceHolderService.theme['accent_800'] as string) ?? '#555962') + '1F', // Adding opacity 12% from accent 800
				colorAccent900: (licenceHolderService.theme['accent_900'] as string) ?? '#464951',
				colorAccent1000: (licenceHolderService.theme['accent_1000'] as string) ?? '#2F3136',
			}}
		>
			<LayoutProvider>
				<NewContainer inheritFrom={sharedResolution.container}>
					<Routes>
						<Route path='/' element={<Shell />}>
							<Route index element={<StartSession flow={KioskSessionFlow.Vcs} />} />
							<Route path='emg' element={<StartSession flow={KioskSessionFlow.QrCode} />} />
							<Route path='qr' element={<QrHook flow={KioskSessionFlow.Vcs} />} />
							<Route path='qr/emg' element={<QrHook flow={KioskSessionFlow.QrCode} />} />
							<Route path='session' element={<Session />} />
							<Route path='thank-you' element={<ThankYou />} />
							<Route path='*' element={<NotFound />} />
						</Route>
						<Route path='*' element={<NotFound />} />
					</Routes>
				</NewContainer>
			</LayoutProvider>
		</CellulaProvider>
	);
}

const elements = {
	desktop: styled.div<{ $noPadding: boolean }>`
		display: flex;
		justify-content: center;
		align-items: center;
		background: ${primitiveColorTokens.primary['800']};
		padding: ${tokens.spacing.x1};
		height: 100%;
		overflow: hidden;
		flex-grow: 1;

		.mobileContainer {
			max-width: 600px;
			width: 100%;
			max-height: 900px;
			height: 100%;
			box-shadow:
				19px 19px 38px ${primitiveColorTokens.primary['900']},
				-19px -19px 38px ${primitiveColorTokens.primary['700']};
			border-radius: 20px;
			${({ $noPadding }) => ($noPadding ? '' : `padding: ${tokens.spacing.x4}`)};
			overflow: hidden;
			display: flex;
			flex-direction: column;
			background: #ffffff;

			& > div:first-of-type {
				border-radius: ${tokens.borderRadius.medium};
				overflow: hidden;
			}
		}
	`,
	mobile: styled.div<{ $noPadding: boolean }>`
		height: 100%;
		display: flex;
		flex-direction: column;
		${({ $noPadding }) => ($noPadding ? '' : `padding: ${tokens.spacing.x4}`)};
		background: #ffffff;
	`,
};

function Shell() {
	const controller = useResolve(SessionController);
	const location = useLocation();

	const noPadding = location.pathname.endsWith('/session');

	controller.setLocale('no-NO');

	return IS_MOBILE ? (
		<elements.mobile $noPadding={noPadding}>
			<Layout />
		</elements.mobile>
	) : (
		<elements.desktop $noPadding={noPadding}>
			<div className='mobileContainer'>
				<Layout />
			</div>
		</elements.desktop>
	);
}

function Layout() {
	const location = useLocation();

	if (location.pathname.endsWith('/session')) {
		return <Outlet />;
	}

	return (
		<>
			<img style={{ margin: '0 15%' }} src='/visiba-care-logotype.svg' alt='Visiba Triage' />
			<Spacer vertical={tokens.spacing.x5} />
			<Outlet />
		</>
	);
}

export default App;
